// Mirrors the API contract. Enums are serialized as strings by the backend, so these are string
// unions rather than numeric enums — "AccrualOnly" in a payload is self-explanatory, 2 is not.

export type AccountType = 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense'
export type NormalBalance = 'Debit' | 'Credit'
export type Basis = 'Both' | 'AccrualOnly' | 'CashOnly'
export type ReportBasis = 'Cash' | 'Accrual'
export type JournalStatus = 'Draft' | 'PendingReview' | 'Posted' | 'Reversed' | 'Voided'
export type JournalBook = 'Bank' | 'Safe' | 'General' | 'Sales' | 'Purchase' | 'Opening' | 'Closing'
export type PartyType = 'Client' | 'Vendor' | 'Employee' | 'Authority'
export type PeriodState = 'Open' | 'Closed' | 'Locked'
export type CashLocationType = 'Bank' | 'Safe'
export type DocumentStatus = 'Logged' | 'Reviewed' | 'Approved' | 'Posted' | 'Rejected'
export type DocumentSource = 'Portal' | 'Issued' | 'Manual' | 'AlxImport'

/** The mandatory classification on any movement of client money (BUILD_PLAN §3.2). */
export type ClientFundNature = 'Fee' | 'Custody' | 'Settlement' | 'CreditNote' | 'DebitNote' | 'Advance'

export interface UserProfile {
  id: string
  email: string
  fullNameEn: string
  fullNameAr?: string | null
  preferredLanguage: string
  mustChangePassword: boolean
  roles: string[]
  /** Drives which nav items and buttons appear. The server still checks every call. */
  permissions: string[]
}

export interface AuthResult {
  accessToken: string
  expiresAt: string
  user: UserProfile
}

export interface AccountNode {
  id: string
  code: string
  nameEn: string
  nameAr: string
  type: AccountType
  normalBalance: NormalBalance
  group: string
  level: number
  isLeaf: boolean
  isActive: boolean
  allowManualEntry: boolean
  natureRequired: boolean
  partyRequired: boolean
  currencyRestriction?: string | null
  balance?: number | null
  children: AccountNode[]
}

/**
 * What a file can be attached to. Mirrors `AttachmentTargets` on the server, which is a closed set
 * because the permission needed to read an attachment is the target's own — there is no
 * `attachments.view`, or a scan on an entry you cannot open would be readable by anyone who can
 * open the intake register.
 */
export type AttachmentTarget = 'DocumentIntake' | 'JournalEntry' | 'CashBookEntry'

export interface Attachment {
  id: string
  entityType: AttachmentTarget
  entityId: string
  /** The name it was uploaded under. Never a path — the server stores it under a generated one. */
  fileName: string
  contentType: string
  sizeBytes: number
  /** SHA-256, hex. The same file twice on one target is refused. */
  contentHash?: string | null
  uploadedBy?: string | null
  uploadedAt: string
}

/**
 * A cost centre — the second of the two analysis dimensions (BUILD_PLAN §3.5). Deliberately flat:
 * the plan is explicit that this is a plain FK column on the journal line, not a hierarchy.
 */
export interface Department {
  id: string
  code: string
  nameEn: string
  nameAr: string
  managerPartyId?: string | null
  displayOrder: number
  isActive: boolean
}

export interface PostableAccount {
  id: string
  code: string
  nameEn: string
  nameAr: string
  type: AccountType
  group: string
  natureRequired: boolean
  partyRequired: boolean
  currencyRestriction?: string | null
  /**
   * False on the per-party sub-ledger accounts. They are only returned at all when the caller asks
   * for `includeSubLedger`, which the books do and the general journal does not.
   */
  allowManualEntry: boolean
  /** Set on an account auto-generated for a party, which is what makes it a sub-ledger account. */
  partyId?: string | null
  /**
   * True on an employee's business loan account. Only ever set when `includeSubLedger` was asked
   * for, and it is what tells the allocation row to offer the instalment fields — the server
   * decides the same thing from the same list, so the field is never shown for terms it would
   * then refuse.
   */
  isEmployeeLoanAccount: boolean
}

export interface Party {
  id: string
  type: PartyType
  code: string
  nameEn: string
  nameAr: string
  vatRegistrationNo?: string | null
  taxId?: string | null
  phone?: string | null
  email?: string | null
  address?: string | null
  paymentTermDays?: number | null
  withholdingTaxRate?: number | null
  usesTrustAccount: boolean
  isActive: boolean
  controlAccountId?: string | null
  custodyAccountId?: string | null
  settlementAccountId?: string | null
  loanAccountId?: string | null
  advanceAccountId?: string | null
}

export interface CashLocation {
  id: string
  code: string
  nameEn: string
  nameAr: string
  type: CashLocationType
  currency: string
  isClientTrust: boolean
  glAccountId: string
  openingBalance: number
}

/** One cash-book row that has not appeared on the statement yet. */
export interface ReconcilingItem {
  id: string
  date: string
  ref?: string | null
  description: string
  receipt: number
  payment: number
  partyName?: string | null
  /** Days since the row was written. An old one is usually a stale cheque or a mistake. */
  ageInDays: number
}

/**
 * The book against the statement.
 *
 * `bookBalance = reconciledBalance + unreconciledReceipts − unreconciledPayments` on every report;
 * `difference` is the statement less what has been ticked, and zero is the point of the screen.
 */
export interface BankReconciliation {
  cashLocationId: string
  locationName: string
  currency: string
  asOf: string
  statementBalance: number
  bookBalance: number
  reconciledBalance: number
  unreconciledReceipts: number
  unreconciledPayments: number
  difference: number
  isReconciled: boolean
  outstanding: ReconcilingItem[]
}

// ─────────────────────────────── Excel import ───────────────────────────────

export type ImportKind = 'ChartOfAccounts' | 'Parties' | 'OpeningBalances' | 'JournalEntries'
export type ImportStatus = 'Validated' | 'Failed' | 'Committed' | 'Discarded'
export type ImportIssueSeverity = 'Error' | 'Warning'

export interface ImportColumn {
  heading: string
  required: boolean
  help?: string | null
  example?: string | null
}

/** One kind of file the office can load, and whether this user is allowed to load it. */
export interface ImportKindInfo {
  kind: ImportKind
  sheet: string
  purpose: string
  /** The permission for what the file would *do* — importing accounts is creating accounts. */
  allowed: boolean
  columns: ImportColumn[]
}

export interface ImportIssue {
  /** 1-based row in the sheet, header included. 0 for a problem with the file itself. */
  rowNumber: number
  column?: string | null
  code: string
  message: string
  severity: ImportIssueSeverity
}

/**
 * The result of checking a file. Nothing has been written at this point — this is the screen the
 * office reads before deciding.
 */
export interface ImportPreview {
  batchId: string
  kind: ImportKind
  fileName: string
  status: ImportStatus
  rowCount: number
  errorCount: number
  warningCount: number
  isCommittable: boolean
  issues: ImportIssue[]
  columns: string[]
  sample: (string | null)[][]
  /** Opening balances only, so the one thing that must be true about the file is on the screen. */
  totalDebit?: number | null
  totalCredit?: number | null
}

export interface ImportResult {
  batchId: string
  status: ImportStatus
  committedCount: number
  journalEntryId?: string | null
  entryNo?: string | null
}

export interface ImportHistoryRow {
  id: string
  kind: ImportKind
  fileName: string
  status: ImportStatus
  rowCount: number
  errorCount: number
  warningCount: number
  committedCount: number
  createdAt: string
  createdBy?: string | null
  committedAt?: string | null
  committedBy?: string | null
  journalEntryId?: string | null
  entryNo?: string | null
}

export interface Currency {
  code: string
  nameEn: string
  nameAr: string
  symbol?: string | null
  decimalPlaces: number
  isBase: boolean
  isActive: boolean
}

/**
 * Effective-dated rate to base: the rate in force on a date is the latest one whose `validFrom` has
 * arrived. One row per currency per date — the API upserts on that pair.
 */
export interface ExchangeRate {
  id: string
  currencyCode: string
  validFrom: string
  /** Units of base currency per one unit of `currencyCode`. Six decimals in the database. */
  rateToBase: number
  source?: string | null
  createdAt: string
  createdBy?: string | null
  modifiedAt?: string | null
  modifiedBy?: string | null
}

export interface CashBookAllocationRow {
  accountCode: string
  accountName: string
  amount: number
  nature?: string | null
  memo?: string | null
  /** The document this allocation cleared, when it settled one. */
  settlesDocNo?: string | null
  /** "12 × 500.00" on a loan issue, so the register shows the plan that was agreed. */
  loanTerms?: string | null
}

export interface CashBookRow {
  id: string
  date: string
  ref?: string | null
  description: string
  receipt: number
  payment: number
  runningBalance: number
  partyName?: string | null
  entryNo?: string | null
  /** The entry this row produced, so the register can drill through to it. */
  journalEntryId?: string | null
  isReconciled: boolean
  allocations: CashBookAllocationRow[]
}

export interface CashBookPage {
  cashLocationId: string
  locationName: string
  currency: string
  from: string
  to: string
  openingBalance: number
  closingBalance: number
  totalReceipts: number
  totalPayments: number
  rows: CashBookRow[]
}

export interface CashAllocationInput {
  accountId: string
  /** Positive sits opposite the cash; negative sits beside it, which is how WHT is entered. */
  amount: number
  clientFundNature?: ClientFundNature | null
  partyId?: string | null
  clientId?: string | null
  departmentId?: string | null
  memo?: string | null
  /** The open item this allocation clears. The server reduces its outstanding balance. */
  settlesOpenItemId?: string | null
  /**
   * The repayment plan, when this allocation is cash lent to an employee (POSTING_MATRIX §A10).
   * Either half is enough — the server works out the other. Allowed only on a line that issues a
   * loan, and refused anywhere else.
   */
  loanInstalmentCount?: number | null
  loanInstalmentAmount?: number | null
}

/**
 * One unsettled document in the sub-ledger — an issued fee statement, a purchase invoice, a
 * custody issue. Picking one on an allocation row is what makes the aging report mean something.
 */
export interface OpenItem {
  id: string
  partyId: string
  partyCode: string
  partyName: string
  docType: string
  docNo: string
  date: string
  dueDate?: string | null
  currency: string
  amount: number
  settled: number
  outstanding: number
  nature?: ClientFundNature | null
  accountId?: string | null
  accountCode?: string | null
  accountName?: string | null
  /** The side a settlement has to land on. 'Credit' for a receivable, 'Debit' for a payable. */
  settledOn: 'Debit' | 'Credit'
  daysOverdue?: number | null
  description?: string | null
}

/** One custody issue or business loan still open against an employee. */
export interface EmployeeScheduleItem {
  openItemId: string
  docType: 'CustodyIssued' | 'LoanIssued'
  docNo: string
  date: string
  currency: string
  amount: number
  settled: number
  outstanding: number
  baseOutstanding: number
  daysOutstanding: number
  instalmentCount?: number | null
  instalmentAmount?: number | null
  journalEntryId?: string | null
  description?: string | null
}

export interface EmployeeScheduleRow {
  partyId: string
  partyCode: string
  partyName: string
  custodyOutstanding: number
  loanOutstanding: number
  advanceBalance: number
  custodyLedgerBalance: number
  loanLedgerBalance: number
  items: EmployeeScheduleItem[]
  totalOwed: number
  /** Documents against the ledger. Non-zero means the account moved without a document behind it. */
  custodyVariance: number
  loanVariance: number
  tiesOut: boolean
  oldestDays: number
}

export interface EmployeeSchedule {
  asOf: string
  employees: EmployeeScheduleRow[]
  totalCustody: number
  totalLoans: number
  totalAdvances: number
  totalOwed: number
  tiesOut: boolean
}

export interface CashRowInput {
  cashLocationId: string
  date: string
  ref?: string | null
  description: string
  receipt: number
  payment: number
  partyId?: string | null
  instrumentNo?: string | null
  allocations: CashAllocationInput[]
}

export interface JournalLineInput {
  accountId: string
  debit: number
  credit: number
  currency?: string | null
  rate?: number | null
  partyId?: string | null
  clientId?: string | null
  departmentId?: string | null
  clientFundNature?: ClientFundNature | null
  memo?: string | null
}

export interface JournalEntryInput {
  book: JournalBook
  postingDate: string
  documentDate?: string | null
  basis: Basis
  description: string
  descriptionAr?: string | null
  reference?: string | null
  autoReverseNextPeriod?: boolean
  lines: JournalLineInput[]
}

export interface PostingProblem {
  code: string
  message: string
  lineNo?: number | null
}

export interface PostingValidation {
  isValid: boolean
  problems: PostingProblem[]
  totalBaseDebit: number
  totalBaseCredit: number
  difference: number
}

export interface JournalSummary {
  id: string
  entryNo?: string | null
  book: JournalBook
  postingDate: string
  basis: Basis
  status: JournalStatus
  description: string
  reference?: string | null
  postedBy?: string | null
  postedAt?: string | null
  /** Who wrote it. A reviewer may not sign off their own entry, so the queue needs it. */
  createdBy?: string | null
  reviewedBy?: string | null
  reviewedAt?: string | null
  amount: number
  lineCount: number
}

/**
 * A draft is waiting on a second person when it has no review stamp. Kept as a function rather
 * than a server field because it is a property of the row the client already holds, and a computed
 * flag on the wire would be a second thing to keep in step with the entry's status.
 */
export function awaitsReview(entry: { status: JournalStatus; reviewedAt?: string | null }): boolean {
  return entry.status === 'Draft' && !entry.reviewedAt
}

/** The office switches a composing screen has to know before it offers a button. */
export interface PostingPolicy {
  requireReviewBeforePost: boolean
  allowBackdatedEntries: boolean
}

/**
 * One line of a posted entry as the detail page shows it.
 *
 * Both amount pairs are carried: `txn*` is what was written on the document and `base*` is what
 * reaches the ledger. They differ only on a foreign-currency line, and that is exactly the line
 * somebody drilling through is checking.
 */
export interface JournalEntryLine {
  id: string
  lineNo: number
  accountId: string
  code: string
  nameEn: string
  nameAr: string
  partyId?: string | null
  partyName?: string | null
  clientId?: string | null
  departmentId?: string | null
  /** The cost centre by name. The id alone tells a reviewer nothing. */
  departmentCode?: string | null
  departmentName?: string | null
  currency: string
  txnDebit: number
  txnCredit: number
  rate: number
  baseDebit: number
  baseCredit: number
  clientFundNature?: ClientFundNature | null
  memo?: string | null
  /** The document this line cleared. The number, not the id, is what tells a reviewer anything. */
  settlesOpenItemId?: string | null
  settlesDocNo?: string | null
  /** The repayment plan agreed, on a line that issued an employee loan (POSTING_MATRIX §A10). */
  loanInstalmentCount?: number | null
  loanInstalmentAmount?: number | null
}

export interface JournalEntryDetail {
  id: string
  entryNo?: string | null
  book: JournalBook
  postingDate: string
  documentDate?: string | null
  basis: Basis
  status: JournalStatus
  description: string
  descriptionAr?: string | null
  reference?: string | null
  /** What produced this entry — a cash-book row, a document intake, a reversal, or nothing. */
  sourceType?: string | null
  sourceId?: string | null
  postedBy?: string | null
  postedAt?: string | null
  /** Who wrote it and who signed it off — the audit answer to "who let this through". */
  createdBy?: string | null
  reviewedBy?: string | null
  reviewedAt?: string | null
  /** The two ends of a reversal. Both stay in the ledger forever; each points at the other. */
  reversalOfId?: string | null
  reversalOfNo?: string | null
  reversedById?: string | null
  reversedByNo?: string | null
  reversalReason?: string | null
  autoReverseNextPeriod: boolean
  /**
   * The accrual's own unwind, which is a different fact from `reversedBy` — the entry stays
   * Posted, because it was right for the period it was posted in (POSTING_MATRIX §C5).
   */
  autoReversedById?: string | null
  autoReversedByNo?: string | null
  lines: JournalEntryLine[]
}

export interface DualBasisLine {
  code: string
  nameEn: string
  nameAr: string
  type: AccountType
  level: number
  isTotal: boolean
  cash: number
  accrual: number
  difference: number
}

export interface ReconciliationCheck {
  resultDifference: number
  receivableMovement: number
  payableMovement: number
  accrualMovement: number
  prepaidMovement: number
  custodyMovement: number
  taxMovement: number
  nonCashCharges: number
  otherMovement: number
  unexplained: number
  holds: boolean
}

export interface ProfitAndLoss {
  from: string
  to: string
  revenue: DualBasisLine[]
  expenses: DualBasisLine[]
  totalRevenue: DualBasisLine
  totalExpenses: DualBasisLine
  netResult: DualBasisLine
  reconciliation: ReconciliationCheck
}

export interface TrialBalanceRow {
  accountId: string
  code: string
  nameEn: string
  nameAr: string
  type: AccountType
  openingDebit: number
  openingCredit: number
  periodDebit: number
  periodCredit: number
  closingDebit: number
  closingCredit: number
}

export interface TrialBalance {
  from: string
  to: string
  basis: ReportBasis
  rows: TrialBalanceRow[]
  totalDebit: number
  totalCredit: number
  isBalanced: boolean
  difference: number
}

export interface ClientFundRow {
  partyId: string
  partyCode: string
  partyName: string
  feesBilled: number
  feesCollected: number
  feesOutstanding: number
  custodyReceived: number
  custodySpent: number
  custodyHeld: number
  settlementsReceived: number
  settlementsPaidOut: number
  settlementsHeld: number
  totalHeld: number
}

export interface ClientFundsControl {
  asOf: string
  clients: ClientFundRow[]
  totalCustodyHeld: number
  totalSettlementsHeld: number
  totalObligation: number
  cashInTrustAccounts: number
  cashInDesignatedSafes: number
  totalClientCash: number
  variance: number
  isHealthy: boolean
}

export interface GaAnalysisRow {
  code: string
  nameEn: string
  nameAr: string
  amount: number
  percentOfTotal: number
  priorPeriodAmount: number
  movement: number
  movementPercent: number
}

export interface GaAnalysis {
  from: string
  to: string
  basis: ReportBasis
  rows: GaAnalysisRow[]
  total: number
  priorTotal: number
  /**
   * Null when every department is included. Named on the report rather than left to the screen,
   * because the Excel export leaves the building and a scoped total that does not say what it is
   * scoped to reads as the whole month's spending.
   */
  departmentLabel?: string | null
}

export interface AgingBucketRow {
  partyId: string
  partyCode: string
  partyName: string
  current: number
  days1To30: number
  days31To60: number
  days61To90: number
  over90: number
  total: number
}

export interface AgingReport {
  asOf: string
  partyType: PartyType
  rows: AgingBucketRow[]
  totals: AgingBucketRow
}

/**
 * One line of the balance sheet. `level` is the account's depth in the chart, kept so the report
 * can be indented rather than flattened — four accounts rolling into one heading is information.
 */
export interface BalanceSheetLine {
  code: string
  nameEn: string
  nameAr: string
  level: number
  isTotal: boolean
  amount: number
}

export interface BalanceSheet {
  asOf: string
  basis: ReportBasis
  assets: BalanceSheetLine[]
  liabilities: BalanceSheetLine[]
  equity: BalanceSheetLine[]
  totalAssets: number
  totalLiabilities: number
  totalEquity: number
  /**
   * This year's profit or loss. Not yet an equity account — it is closed at year end — so it sits
   * on its own line. Assets = liabilities + equity + this.
   */
  currentYearResult: number
  difference: number
  isBalanced: boolean
}

export type CashFlowActivity = 'Operating' | 'Investing' | 'Financing'

export interface CashFlowLine {
  code: string
  nameEn: string
  nameAr: string
  activity: CashFlowActivity
  inflows: number
  outflows: number
  net: number
}

export interface CashFlowStatement {
  from: string
  to: string
  operating: CashFlowLine[]
  investing: CashFlowLine[]
  financing: CashFlowLine[]
  openingCash: number
  closingCash: number
  netOperating: number
  netInvesting: number
  netFinancing: number
  netChange: number
  calculatedClosingCash: number
  difference: number
  reconciles: boolean
}

/**
 * One tax account over a period. `charged` is the movement that increases the obligation and
 * `settled` the one that reduces it, whichever side of the account that happens to be — so the
 * office reads two positive numbers instead of working out a sign.
 */
export interface TaxSummaryRow {
  code: string
  name: string
  opening: number
  charged: number
  settled: number
  closing: number
}

export interface TaxSummary {
  from: string
  to: string
  vat: TaxSummaryRow[]
  withholding: TaxSummaryRow[]
  /** Anything under the tax-authority group that is neither VAT nor withholding. */
  other: TaxSummaryRow[]
}

export interface LedgerRow {
  date: string
  entryNo?: string | null
  book: JournalBook
  description: string
  reference?: string | null
  partyName?: string | null
  debit: number
  credit: number
  runningBalance: number
  journalEntryId: string
  basis: Basis
  memo?: string | null
}

export interface AccountStatement {
  accountId: string
  code: string
  nameEn: string
  nameAr: string
  from: string
  to: string
  basis: ReportBasis
  openingBalance: number
  closingBalance: number
  totalDebit: number
  totalCredit: number
  rows: LedgerRow[]
}

export interface FiscalPeriod {
  id: string
  year: number
  month: number
  startDate: string
  endDate: string
  state: PeriodState
  closedBy?: string | null
  lockedBy?: string | null
}

export interface CloseCheckItem {
  key: string
  label: string
  isBlocking: boolean
  isDone: boolean
  checkedBy?: string | null
  checkedAt?: string | null
}

/**
 * Accruals in this period still waiting to unwind into the next one (POSTING_MATRIX §C5).
 * `nextPeriodLabel` is null at the end of the last year that has periods — the fix is to open next
 * year, which the same screen can do.
 */
export interface AutoReversalPreview {
  pendingCount: number
  pendingBaseAmount: number
  nextPeriodLabel?: string | null
  nextPeriodAcceptsPostings: boolean
}

export interface PeriodCloseStatus {
  periodId: string
  label: string
  state: PeriodState
  draftEntryCount: number
  unpostedDocumentCount: number
  clientFundsVariance: number
  trialBalanceBalances: boolean
  autoReversals: AutoReversalPreview
  checklist: CloseCheckItem[]
  canClose: boolean
}

export interface DocumentRow {
  id: string
  source: DocumentSource
  docNo: string
  date: string
  status: DocumentStatus
  currency: string
  amount: number
  vatAmount: number
  totalAmount: number
  withholdingAmount: number
  partyId?: string | null
  partyName?: string | null
  vatRegistrationNo?: string | null
  journalEntryId?: string | null
  rejectionReason?: string | null
  notes?: string | null
  /** How many files are attached. "Is this one scanned?" is the register's own question. */
  attachmentCount: number
}

/**
 * What a new document starts from, as fractions — 0.14, not 14. Editable on the admin Settings
 * screen; every screen that pre-fills a tax figure reads them from `GET /api/tax-defaults` rather
 * than carrying its own copy.
 */
export interface TaxDefaults {
  vatRate: number
  whtRate: number
}

export interface IntakeAgeBucket {
  status: DocumentStatus
  count: number
  total: number
  oldestDays: number
}

export interface AppUserRow {
  id: string
  email: string
  fullNameEn: string
  fullNameAr?: string | null
  isActive: boolean
  mustChangePassword: boolean
  lastLoginAt?: string | null
  preferredLanguage: string
  isLockedOut: boolean
  roles: string[]
}

export interface RoleRow {
  id: string
  name: string
  description?: string | null
  isSystemRole: boolean
  userCount: number
  permissions: string[]
}

export interface AuditRow {
  id: string
  tableName: string
  recordId: string
  action: string
  before?: string | null
  after?: string | null
  changedColumns?: string | null
  userName?: string | null
  timestamp: string
  ipAddress?: string | null
  summary?: string | null
}

export interface Paged<T> {
  total: number
  page: number
  size: number
  items: T[]
}
